import { createSlice } from "@reduxjs/toolkit";

const STORAGE_KEY = "nischalam_cart_v1";

/**
 * A cart line is uniquely identified by `${productId}__${size}` so that the
 * same product added under different packaging sizes is kept as separate rows.
 */
export const lineId = (productId, size) => `${productId}__${size ?? ""}`;

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { items: {}, order: [] };
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.items && Array.isArray(parsed.order)) {
      return parsed;
    }
  } catch { /* ignore */ }
  return { items: {}, order: [] };
}

const initialState = loadFromStorage();

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addItem: {
      reducer(state, action) {
        const { id, productId, name, size, unitPrice, image } = action.payload;
        if (state.items[id]) {
          state.items[id].quantity += 1;
        } else {
          state.items[id] = {
            id, productId, name, size,
            unitPrice: Number(unitPrice) || 0,
            image: image || "",
            quantity: 1,
          };
          state.order.push(id);
        }
      },
      prepare({ productId, name, size, unitPrice, image }) {
        return { payload: { id: lineId(productId, size), productId, name, size, unitPrice, image } };
      },
    },
    incrementItem(state, action) {
      const id = action.payload;
      if (state.items[id]) state.items[id].quantity += 1;
    },
    decrementItem(state, action) {
      const id = action.payload;
      const line = state.items[id];
      if (!line) return;
      if (line.quantity <= 1) {
        delete state.items[id];
        state.order = state.order.filter((x) => x !== id);
      } else {
        line.quantity -= 1;
      }
    },
    removeItem(state, action) {
      const id = action.payload;
      delete state.items[id];
      state.order = state.order.filter((x) => x !== id);
    },
    clearCart() {
      return { items: {}, order: [] };
    },
  },
});

export const { addItem, incrementItem, decrementItem, removeItem, clearCart } = cartSlice.actions;

/** Selectors */
export const selectCartLines = (state) => state.cart.order.map((id) => state.cart.items[id]).filter(Boolean);
export const selectCartLine  = (state, id) => state.cart.items[id];
export const selectCartCount = (state) => state.cart.order.reduce(
  (sum, id) => sum + (state.cart.items[id]?.quantity || 0), 0
);
export const selectCartTotal = (state) => state.cart.order.reduce(
  (sum, id) => {
    const l = state.cart.items[id];
    return l ? sum + l.unitPrice * l.quantity : sum;
  }, 0
);

/** Attach once from store/index.js — persists cart on every mutation. */
export function attachCartPersistence(store) {
  let prev;
  store.subscribe(() => {
    const next = store.getState().cart;
    if (next !== prev) {
      prev = next;
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    }
  });
}

export default cartSlice.reducer;
